import React, { FC, useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, Input, TextArea } from "@pd/pd-ui-kit";
import { Col, ConfigProvider, Divider, Flex, Row } from "antd";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import useCourseLevelsSelectOptions from "~/entity/Courses/hooks/useCourseLevelsSelectOptions";
import useAddEmployeeExpectations from "~/pages/Editor/EditorStaff/hooks/useAddEmployeeExpectations";
import useUpdateEmployeeExpectations from "~/pages/Editor/EditorStaff/hooks/useUpdateEmployeeExpectations";
import { staffFormSchema } from "~/components/Forms/StaffForm/yupSchema";
import { onScrollBottomIndicator } from "~/hooks/selectIndicatorValue";
import { useGetPositionsForSelect } from "~/hooks/useGetPositionsForSelect";
import { language } from "~/libs/getData";
import { useSelectedPositionsCourses } from "~/pages/StaffBonus/hooks/useSelectPositionsCourse";
import { Select } from "~/shared/ui/Select";

import s from "./StaffForm.less";

interface IProps {
  setCloseModal: () => void;
  refetch;
  formType?: string;
  Staff?;
  dataDealersCategories?: string[];
  prevElemId?: string;
  isEditable?: boolean;
}

const onPopupScrollBottom =
  (callback?: () => void) => (e: React.UIEvent<HTMLDivElement>) => {
    if (!callback) return;
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      callback();
    }
  };

const StaffForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  Staff = {},
  dataDealersCategories,
}) => {
  const { t } = useTranslation();
  const isEn = language === "en";

  const categories = Array.isArray(dataDealersCategories)
    ? dataDealersCategories.map((el) => ({
        value: el,
        label: el,
      }))
    : [];

  const indicatorScrolledInstance = onScrollBottomIndicator();
  const positionsScrolledInstance = useGetPositionsForSelect(true);

  // Выбранные значения подмешиваем в опции: справочники грузятся постранично,
  // и без этого label предзаполненного значения может не найтись среди опций
  const positionsOptions = useMemo(() => {
    const selected = Array.isArray(Staff.positions)
      ? Staff.positions.map(({ instanceId, code, name, nameEn }) => ({
          value: instanceId,
          label: isEn ? `${code}. ${nameEn}` : `${code}. ${name}`,
        }))
      : [];
    const merged = [...selected];
    positionsScrolledInstance.positions.forEach((opt) => {
      if (!merged.some((o) => o.value === opt.value)) merged.push(opt);
    });
    return merged;
  }, [Staff.positions, positionsScrolledInstance.positions, isEn]);

  const indicatorsOptions = useMemo(() => {
    const merged = Staff?.StandardCountIndicatorOption
      ? [Staff.StandardCountIndicatorOption]
      : [];
    indicatorScrolledInstance.indicatorsForSelect.forEach((opt) => {
      if (!merged.some((o) => o.value === opt.value)) merged.push(opt);
    });
    return merged;
  }, [
    Staff?.StandardCountIndicatorOption,
    indicatorScrolledInstance.indicatorsForSelect,
  ]);

  const { id, createdAt } = Staff;
  const { mutate: updateExpectations, isLoading: isUpdating } =
    useUpdateEmployeeExpectations();
  const { mutate: addExpectations, isLoading: isAdd } =
    useAddEmployeeExpectations();

  const [isCombining, setIsCombining] = useState<boolean>(!!Staff?.isCombining);
  const [combineTraining, setCombineTraining] = useState<boolean>(
    !!Staff?.combineTraining,
  );

  const [combineCertification, setCombineCertification] = useState<boolean>(
    !!Staff?.combineCertification,
  );
  const [categoryDealerException, seCategoryDealerException] =
    useState<boolean>(!!Staff?.isCategoryDealerException);
  const [ignoreTrainingStart, setIgnoreTrainingStart] = useState<boolean>(
    !!Staff?.ignoreTrainingStart,
  );
  const onCombineTraining = () => setCombineTraining((p) => !p);
  const onCombineTraining2 = () => setCombineCertification((p) => !p);
  const onCategoryDealerException = () => seCategoryDealerException((p) => !p);
  const onIgnoreTrainingStart = () => setIgnoreTrainingStart((p) => !p);

  const { levelsSelectOptions } = useCourseLevelsSelectOptions();

  const [usePercentageForEducation, setUsePercentageForEducation] =
    useState<boolean>(Staff?.isEducationRequirementPercentage ?? false);
  const [usePercentageForCertification, setUsePercentageForCertification] =
    useState<boolean>(Staff?.isCertificationRequirementPercentage ?? false);

  const toggleEducationRequirementType = () =>
    setUsePercentageForEducation((p) => !p);
  const toggleCertificationRequirementType = () =>
    setUsePercentageForCertification((p) => !p);

  const { handleSubmit, control, errors, setValue, watch } = useForm({
    defaultValues: {
      method: Staff?.method ?? "",
      methodEn: Staff?.methodEn ?? "",
      positions: Array.isArray(Staff.positions)
        ? Staff.positions.map(({ instanceId }) => instanceId)
        : [],
      StandardCountIndicatorId:
        Staff?.StandardCountIndicatorOption?.value ?? null,
      educationRequirement: Staff?.educationRequirement ?? 0,
      certificationRequirement: Staff?.certificationRequirement ?? 0,
      laCourses: Staff?.laCourses ?? [],
      requiredCoursesCount: Staff?.requiredCoursesCount ?? null,
      requiredCoursesLevelId: Staff?.requiredCoursesLevelId ?? null,
      categoryDealerExceptions: Staff?.categoryDealerExceptions ?? [],
    },
    resolver: yupResolver(staffFormSchema(t)),
  });

  const { coursesOptions, isLoading } = useSelectedPositionsCourses(watch);

  useEffect(() => {
    const currentEducation = control.getValues().educationRequirement;
    const currentCertification = control.getValues().certificationRequirement;

    setValue(
      "educationRequirement",
      usePercentageForEducation
        ? currentEducation || Staff?.educationRequirement || 0
        : currentEducation || Staff?.educationRequirement || 0,
    );

    setValue(
      "certificationRequirement",
      usePercentageForCertification
        ? currentCertification || Staff?.certificationRequirement || 0
        : currentCertification || Staff?.certificationRequirement || 0,
    );
  }, [usePercentageForEducation, usePercentageForCertification, setValue]);

  const onSubmit = (data) => {
    const formData = {
      method: data.method,
      methodEn: data.methodEn,
      combineTraining: combineTraining,
      combineCertification: combineCertification,
      isCategoryDealerException: categoryDealerException,
      isCombining: isCombining,
      standardCountIndicatorId: Number(data.StandardCountIndicatorId || 0),
      categoryDealerExceptions: data.categoryDealerExceptions ?? [],
      educationRequirement: Number(data.educationRequirement),
      isEducationRequirementPercentage: usePercentageForEducation,
      certificationRequirement: Number(data.certificationRequirement),
      isCertificationRequirementPercentage: usePercentageForCertification,
      positionIds: (data.positions ?? []).map(Number),
      laCourses: (data.laCourses ?? []).map(Number),
      requiredCoursesCount: Number(data.requiredCoursesCount) || null,
      requiredCoursesLevelId: Number(data.requiredCoursesLevelId) || null,
      ignoreTrainingStart: ignoreTrainingStart,
      instanceId: Number(Staff.instanceId || 0),
    };

    if (!!id) {
      updateExpectations(formData, {
        onSuccess: () => {
          refetch();
          setCloseModal();
        },
        onError: (error) => {
          console.error("Ошибка при обновлении:", error);
        },
      });
    } else {
      addExpectations(formData, {
        onSuccess: () => {
          refetch();
          setCloseModal();
        },
        onError: (error) => {
          console.error("Ошибка при обновлении:", error);
        },
      });
    }
  };

  return (
    <ConfigProvider
      theme={{ token: { fontFamily: 'LADA_Pragmatica, "Roboto", sans-serif' } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex vertical gap={16}>
          <Controller
            name="positions"
            control={control}
            render={({ value, onChange }) => (
              <div className={s.field}>
                <span className={s.fieldLabel}>
                  {t("editorStaff.position")}
                  <span className={s.red}>*</span>
                </span>
                <Select
                  mode="multiple"
                  value={value ?? []}
                  onChange={onChange}
                  options={positionsOptions}
                  showSearch
                  filterOption={false}
                  onSearch={positionsScrolledInstance.searchText}
                  onPopupScroll={onPopupScrollBottom(
                    positionsScrolledInstance.intersectionObserver,
                  )}
                  status={errors.positions ? "error" : undefined}
                />
              </div>
            )}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Controller
                name="method"
                control={control}
                render={({ value, onChange }) => (
                  <div className={s.field}>
                    <span className={s.fieldLabel}>
                      {t("editorStaff.method")}
                    </span>
                    <TextArea
                      value={value ?? ""}
                      onChange={onChange}
                      status={errors.method ? "error" : "default"}
                      errorText={errors.method?.message}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                  </div>
                )}
              />
            </Col>
            <Col span={12}>
              <Controller
                name="methodEn"
                control={control}
                render={({ value, onChange }) => (
                  <div className={s.field}>
                    <span className={s.fieldLabel}>
                      {t("editorStaff.methodEn")}
                    </span>
                    <TextArea
                      value={value ?? ""}
                      onChange={onChange}
                      status={errors.methodEn ? "error" : "default"}
                      errorText={errors.methodEn?.message}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                    />
                  </div>
                )}
              />
            </Col>
          </Row>

          <Controller
            name="StandardCountIndicatorId"
            control={control}
            render={({ value, onChange }) => (
              <div className={s.field}>
                <span className={s.fieldLabel}>
                  Численность: норматив<span className={s.red}>*</span>
                </span>
                <Select
                  value={value ?? undefined}
                  onChange={onChange}
                  options={indicatorsOptions}
                  showSearch
                  filterOption={false}
                  onSearch={indicatorScrolledInstance.searchIndicator}
                  onPopupScroll={onPopupScrollBottom(
                    indicatorScrolledInstance.onScrollBottomIndicators,
                  )}
                  allowClear
                />
              </div>
            )}
          />

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />

          <div className={s.titleText}>
            Требование по обучению<span className={s.red}>*</span>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Flex align="center" gap={8}>
                <Checkbox
                  checked={usePercentageForEducation}
                  onChange={toggleEducationRequirementType}
                  label="ПРОЦЕНТ"
                />
                <Controller
                  name="educationRequirement"
                  control={control}
                  render={({ value, onChange }) => (
                    <Input
                      type="number"
                      min={0}
                      max={usePercentageForEducation ? 100 : undefined}
                      value={value ?? ""}
                      onChange={onChange}
                      disabled={!usePercentageForEducation}
                    />
                  )}
                />
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" gap={8}>
                <Checkbox
                  checked={!usePercentageForEducation}
                  onChange={toggleEducationRequirementType}
                  label="НОРМАТИВ"
                />
                <Controller
                  name="educationRequirement"
                  control={control}
                  render={({ value, onChange }) => (
                    <Input
                      type="number"
                      value={value ?? ""}
                      onChange={onChange}
                      disabled={usePercentageForEducation}
                    />
                  )}
                />
              </Flex>
            </Col>
          </Row>

          <Controller
            name="laCourses"
            control={control}
            render={({ value, onChange }) => (
              <div className={s.field}>
                <span className={s.fieldLabel}>Выберите курс</span>
                <Select
                  mode="multiple"
                  value={value ?? []}
                  onChange={onChange}
                  options={coursesOptions}
                  loading={isLoading}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            )}
          />

          <Row gutter={16}>
            <Col span={12}>
              <Controller
                name="requiredCoursesCount"
                control={control}
                render={({ value, onChange }) => (
                  <div className={s.field}>
                    <span className={s.fieldLabel}>
                      {t("editorStaff.requiredCoursesCount")}
                    </span>
                    <Input
                      type="number"
                      min={1}
                      value={value ?? ""}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </Col>
            <Col span={12}>
              <Controller
                name="requiredCoursesLevelId"
                control={control}
                render={({ value, onChange }) => (
                  <div className={s.field}>
                    <span className={s.fieldLabel}>
                      {t("editorStaff.requiredCoursesLevel")}
                    </span>
                    <Select
                      value={value ?? undefined}
                      onChange={onChange}
                      options={levelsSelectOptions}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                    />
                  </div>
                )}
              />
            </Col>
          </Row>

          <div>
            <Checkbox
              checked={combineTraining}
              onChange={onCombineTraining}
              label={t("editorStaff.considerTrainingInStatistics")}
            />
          </div>

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />

          <div className={s.titleText}>
            Требование к сертификации<span className={s.red}>*</span>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Flex align="center" gap={8}>
                <Checkbox
                  checked={usePercentageForCertification}
                  onChange={toggleCertificationRequirementType}
                  label="ПРОЦЕНТ"
                />
                <Controller
                  name="certificationRequirement"
                  control={control}
                  render={({ value, onChange }) => (
                    <Input
                      type="number"
                      min={0}
                      max={usePercentageForCertification ? 100 : undefined}
                      value={value ?? ""}
                      onChange={onChange}
                      disabled={!usePercentageForCertification}
                    />
                  )}
                />
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" gap={8}>
                <Checkbox
                  checked={!usePercentageForCertification}
                  onChange={toggleCertificationRequirementType}
                  label="НОРМАТИВ"
                />
                <Controller
                  name="certificationRequirement"
                  control={control}
                  render={({ value, onChange }) => (
                    <Input
                      type="number"
                      value={value ?? ""}
                      onChange={onChange}
                      disabled={usePercentageForCertification}
                    />
                  )}
                />
              </Flex>
            </Col>
          </Row>

          <div>
            <Checkbox
              checked={combineCertification}
              onChange={onCombineTraining2}
              label={t("editorStaff.considerTrainingInStatistics")}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            <Checkbox
              checked={ignoreTrainingStart}
              onChange={onIgnoreTrainingStart}
              label={t("editorStaff.ignoreTrainingStart")}
            />
          </div>

          <Divider style={{ margin: 0 }} />

          <div>
            <Checkbox
              checked={categoryDealerException}
              onChange={onCategoryDealerException}
              label={t("editorStaff.isCategoryDealerException")}
            />
          </div>

          <Controller
            name="categoryDealerExceptions"
            control={control}
            render={({ value, onChange }) => (
              <div className={s.field}>
                <span className={s.fieldLabel}>Выберите категорию ДЦ</span>
                <Select
                  mode="multiple"
                  value={value ?? []}
                  onChange={onChange}
                  options={categories}
                  showSearch
                  optionFilterProp="label"
                />
              </div>
            )}
          />

          <Divider style={{ marginTop: 10, marginBottom: 10 }} />

          <span className={s.fieldLabel}>
            {t("editorGR.createdAt")}:{" "}
            {format(id ? new Date(createdAt) : new Date(), "dd.MM.yyyy")}
          </span>

          <Flex justify="space-between" align="center">
            <span className={s.fieldLabel}>{t("q.asterisk")}</span>
            <Button htmlType={"submit"} loading={isUpdating || isAdd}>
              {t("ui.save")}
            </Button>
          </Flex>
        </Flex>
      </form>
    </ConfigProvider>
  );
};
export default StaffForm;
